// ── Feature flag registry ────────────────────────────────────────────
//
// Centralised "what's gated on what" map. Replaces ad-hoc per-feature
// domain checks (`if (isBespokeThreadsDomain) ...`) scattered through
// the codebase. One file lists every gated feature, so:
//
//   - Engineers can see at a glance what's pre-GA on bespokethreads.
//   - Graduating a feature is a one-line change (`"staging"` → `true`).
//   - Rolling back is a one-line change (`true` → `"staging"`).
//   - Killing a feature is a one-line change (anything → `false`).
//   - Adding a new gated feature is one line in `FLAG_REGISTRY` + one
//     `useFeatureFlag()` call at the consumer.
//
// Flag values can be:
//
//   true        → GA, always on
//   false       → off everywhere (kill switch)
//   "staging"   → on only for staging tenants (see `stagingTenants.js`)
//   (ctx)=>bool → custom predicate. Receives `{ user }`. Use sparingly
//                 for one-offs like "only this email" or "only enterprise
//                 admins on plan X". If you find yourself writing the
//                 same predicate twice, lift it to a named helper.
//
// CALLING THE FLAG:
//
//   import { useFeatureFlag } from "../helpers/featureFlags";
//   const quotesEnabled = useFeatureFlag("quotes.enabled");
//   return quotesEnabled ? <Quotes /> : null;
//
// OR INSIDE another `useMemo` (e.g. `useMainNavVisibility`):
//
//   const flags = useFeatureFlags();
//   if (!flags["quotes.enabled"]) return false;
//
// CAVEATS:
//
//   - This layer hides UI; it does NOT block routes. A user who
//     bookmarks `/quotes/new` will still hit the page on a non-staging
//     tenant. To gate route access, the route component needs its own
//     `useFeatureFlag` + `<Navigate />`.
//   - Backend isn't aware of these flags. A determined user can still
//     POST to a gated endpoint. Treat this as UX gating, not a security
//     boundary. Server-side enforcement is a separate concern.
//   - Flag values are baked into the bundle. Flipping a flag requires
//     a redeploy. If you ever need flip-without-deploy, swap
//     `FLAG_REGISTRY` for a Firestore-backed fetch — the consumer API
//     (`useFeatureFlag`) doesn't have to change.

import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { isBigFrogStagingHost, isStagingTenant } from "./stagingTenants";
import { GIRLTRIBE_ENTERPRISE_ID } from "./pretreat";
import {
  currentDomain,
  isBespokeThreadsDomain,
  isBigFrogEnterprise,
  isDevDomain,
  isLocalhost,
  isMerchmonkiesDomian,
  isOmniCommerceContext,
  isPromiseApparelDomian,
  OMNICOMMERCE_ENTERPRISE_ID,
} from "../utils";
import { isTheHomeTUser } from "../constants/theHomeT";
import {
  DEV_WAREHOUSE_FRESH_EVENT,
  getDevWarehouseState,
  isDevWarehouseFresh,
} from "./devWarehouseFresh";

/** Bespoke Threads enterprise — 3PL seeded on by default (INV.1). */
export const BESPOKE_THREADS_ENTERPRISE_ID = "cfbZh6XFBG3usCcUwpRE";

/** Promise Apparel — the DTG cost calculator is on for this account only. */
export const PROMISE_APPAREL_ENTERPRISE_ID = "CHg8Uiw4wsMUr8OnKllsNPw6NPR2";

/** Walnut Creek Foods — the ERP white label label program runs here first. */
export const WALNUT_CREEK_ENTERPRISE_ID = "FPVXx7M3WAte1R2MG2tv";

const featureMapHas = (features, key) => {
  if (Array.isArray(features)) {
    return features.includes(key) ? true : null;
  }
  if (features && typeof features === "object" && key in features) {
    return !!features[key];
  }
  return null;
};

/**
 * Admin toggle lives on enterprise.features.3pl.
 * Explicit false wins (admin turned it off). Missing key: Bespoke /
 * staging domains default on until the setting is written.
 */
const enterpriseHas3pl = (enterprise, enterpriseId) => {
  const eid = enterpriseId || enterprise?.id || enterprise?.enterpriseId;
  const explicit = featureMapHas(enterprise?.features, "3pl");
  if (explicit !== null) return explicit;
  if (eid === BESPOKE_THREADS_ENTERPRISE_ID) return true;
  return false;
};

/** INV.2 — warehouse flag. Explicit wins; 3PL on grandfathers warehouse on. */
const enterpriseHasWarehouse = (enterprise, enterpriseId) => {
  const explicit = featureMapHas(enterprise?.features, "warehouse");
  if (explicit !== null) return explicit;
  return enterpriseHas3pl(enterprise, enterpriseId);
};

/**
 * ERP.1 — the ERP white label label program (D365 ingest, Needs labels,
 * work id scanning, print on roll labels rather than the QR barcode labels).
 * Admin toggle lives on enterprise.features.erp. Explicit wins; otherwise on
 * for Walnut Creek Foods, the first tenant on the program.
 */
const enterpriseHasErp = (enterprise, enterpriseId) => {
  const eid = enterpriseId || enterprise?.id || enterprise?.enterpriseId;
  const explicit = featureMapHas(enterprise?.features, "erp");
  if (explicit !== null) return explicit;
  return eid === WALNUT_CREEK_ENTERPRISE_ID;
};

/**
 * The registry. Add new flags here; flip values here to graduate / roll
 * back / kill features. Keep the flag IDs namespaced (`domain.feature`)
 * so a glance at the keys shows what surfaces are gated.
 */
const FLAG_REGISTRY = {
  // Quotes & Sales Orders — GA for all tenants. Originally added
  // by `feature/quotes-sales-orders` (PR #525) as an inline
  // `isBespokeThreadsDomain || isLocalhost` JSX check in
  // SidebarContent.js, ported here so the gate survives the chrome
  // overhaul's sidebar rewrite.
  "quotes.enabled": true,

  // Production Calendar — GA for all tenants. Shipped with quotes/SO on
  // bespokethreads first; nav was always wired in mainNav.js but
  // graduates here so rollout matches quotes.enabled.
  "productionCalendar.enabled": true,

  // Customer Messaging Inbox — email-backed CRM threads. Backend and client
  // wiring are live, so expose the inbox + quote-thread surfaces for all
  // tenants via the existing UI gates that already consume this flag.
  "messaging.enabled": true,

  // CUS.1 — Customers list (index page + nav). Staging ring first
  // (Bespoke Threads / Big Frog admin / localhost), then graduate to GA.
  // Big Frog shops log in on bigfrog-*.myriverr.com (and HQ opens a shop
  // by switching onto that host). They get Contacts without the rest of
  // the staging ring, so quote pricing v3 stays off there.
  "customers.enabled": (ctx) =>
    isStagingTenant() ||
    isBigFrogStagingHost(currentDomain) ||
    isBigFrogEnterprise(ctx?.user?.enterprise),

  // NAV.1 — Marketplace (formerly Third Party Catalog). Own flag so
  // we can roll out per tenant when the supply side is ready. Nav
  // entry is commented out in mainNav.js regardless; route stays live.
  "marketplace.enabled": false,

  // Shopify shipping import — GA for all tenants (2026-06-30). Pushes
  // popup-store shipping options into a seller's connected Shopify store
  // as a per-enterprise delivery profile; sellers set their own prices in
  // Shopify. Backend mirrors this GA gate by enterpriseId (see
  // riverr-enterprise-graphql configs/staging.ts).
  "shopifyShipping.enabled": true,

  // PRC.2.1 — shared pricing engine (packages/pricing-core). Staging-only
  // while shadow-mode deltas are reviewed.
  //
  // pricingV3.quoteEngine: quote builder prices line items through
  //   pricing-core with design-group tier pooling (aggregate quantities,
  //   multi-placement, combo keys) instead of the legacy per-line lookup.
  // pricingV3.onboarding: Step 6 pricing wizard (R1, 7 methods). GA so
  //   new hosts see the wizard — not just the staging ring.
  // pricingV3.enabled: staging /pricing-tables-v3 only. Settings
  //   /pricing-tables stays on PricingDialog until the cutover gate.
  // pricingV3.methods10And20: R2 — Digital Printing + Big Frog Hard Goods
  //   in the new wizard. Off until the R1 soak is green.
  "pricingV3.quoteEngine": "staging",
  "pricingV3.onboarding": true,
  "pricingV3.enabled": "staging",
  "pricingV3.methods10And20": false,

  // POS.1 — Counter register (standalone /pos). Staging ring
  // (Big Frog / Bespoke Threads / Girl Tribe / localhost), plus
  // Merch Monkies (app.merchmonkies.com).
  "pos.enabled": () => isStagingTenant() || isMerchmonkiesDomian,

  // Quote line Cost Calculator — DTG cost from the printer's own labor, ink,
  // pretreat, machine and overhead numbers (Promise Apparel's estimator).
  // Promise Apparel, plus Bespoke Threads (Riverr's own test account) so
  // changes can be tested live before Promise sees them.
  "quotes.costCalculator": (ctx) => {
    const user = ctx?.user || {};
    if (isBespokeThreadsDomain) return true;
    return [user.enterpriseId, user.enterprise?.id, user.uid].includes(
      PROMISE_APPAREL_ENTERPRISE_ID
    );
  },

  // TIX.1 — Manufacturing Tickets rail (kanban of auto_pos batches).
  "tickets.rail": true,

  // ERP.1 — ERP white label label program. Turns on the Batches / Needs
  // labels nav for a plan that otherwise hides Manufacturing, the per batch
  // Station button (scan the work order → pull view), and hides the QR
  // barcode label download surfaces that this tenant does not use. Keyed on
  // enterprise.features.erp; Walnut Creek Foods on by id until it is written.
  "erp.labelProgram": (ctx) =>
    enterpriseHasErp(ctx?.user?.enterprise, ctx?.user?.enterpriseId),

  // Base product editor unification. One editor for every product, forced
  // wizard on create and free navigation on edit, with sections chosen by the
  // product category. Phase 2 — generic (apparel / 3D) path lives inside the
  // unified shell with status glyphs, wizard mode, and a locked step list.
  // Print products still fall through to the specialty wizard (next phase).
  // Staging tenants only (Bespoke Threads + the staging ring) until proven.
  "blankEditor.unified": "staging",

  // INS.1 — Inserts and tags (catalog, rules, ready-to-ship list).
  // GA for all tenants (2026-10-27). Each enterprise turns the program
  // on at enterprises/{id}/settings/inserts.enabled.
  "inserts.enabled": true,

  // Brother presses and the scan station. Bespoke Threads and Promise Apparel.
  "brother.presses": (ctx) => {
    const ids = [
      ctx?.user?.enterpriseId,
      ctx?.user?.enterprise?.id,
      ctx?.user?.uid,
    ];
    return (
      isBespokeThreadsDomain ||
      isPromiseApparelDomian ||
      isLocalhost ||
      ids.includes(BESPOKE_THREADS_ENTERPRISE_ID) ||
      ids.includes(PROMISE_APPAREL_ENTERPRISE_ID)
    );
  },

  // DSN.2 — multi-design product options in the catalog customizer.
  // GA for all tenants (2026-08-06). Backend already accepts designSet;
  // UI entry was staging-ring only until this graduate.
  "catalog.multiDesign": true,

  // DTG pretreat rules (Settings › Manufacturing › DTG options).
  // Girl Tribe only — they use PT Yes/No on barcodes and batch filters.
  "dtg.pretreatRules": (ctx) =>
    ctx?.user?.enterpriseId === GIRLTRIBE_ENTERPRISE_ID ||
    ctx?.user?.enterprise?.id === GIRLTRIBE_ENTERPRISE_ID,

  // The Home T only — move every Shopify product (including unsynced)
  // to the Rakiline fulfillment-service location. Listings page also
  // shows this when an admin is viewing The Home T via sharedBy.
  "listings.moveAllToAppLocation": (ctx) => isTheHomeTUser(ctx?.user),

  // Fabritech / OmniCommerce pre-ship QC table. Explicit enterprise.features.qc_station
  // wins; otherwise on for OmniCommerce (fabritech.myriverr.com).
  "qc.preShipStation": (ctx) => {
    const enterprise = ctx?.user?.enterprise;
    const explicit = featureMapHas(enterprise?.features, "qc_station");
    if (explicit !== null) return explicit;
    return isOmniCommerceContext(enterprise);
  },

  // Pricing strategy — Budget / Standard / Premium markup tiers on
  // Blank settings → Suggested Retail (sets the blank's default margin)
  // and on the create product pricing card, plus per product fees
  // (license fees) that come out of profit. OmniCommerce / Fabritech only.
  "pricing.strategy": (ctx) =>
    isOmniCommerceContext(ctx?.user?.enterprise) ||
    ctx?.user?.enterpriseId === OMNICOMMERCE_ENTERPRISE_ID,

  // Duplicate placement (Settings → Catalog): copy a placement's print setup
  // and prices to another and move designs onto it. GA for every enterprise
  // admin; enterprise.features.duplicate_placement: false turns it off for one
  // account. The Node API checks the same rule.
  "placements.duplicate": (ctx) => {
    const user = ctx?.user;
    if (!user?.isEnterpriseAdmin) return false;
    return featureMapHas(user?.enterprise?.features, "duplicate_placement") !== false;
  },

  // STK.1 — SDF die-cut cutline + material preview (sheen). GA for all tenants.
  "stickers.sdfCutline": true,

  // PRINT.1 — the generic PrintSpec designer (pages/PrintProduct) for die cut,
  // kiss cut, roll label, poster and postcard routes. Off = the old sticker
  // and poster editors (postcards have no old editor and always use it).
  "printProducts.unifiedDesigner": true,

  // INV.2 — hold your own stock. Explicit features.warehouse; 3PL on
  // grandfathers this on so existing programs keep their floor.
  "inventory.warehouse": (ctx) => {
    if (isDevWarehouseFresh()) return !!getDevWarehouseState().warehouseEnabled;
    const enterprise = ctx?.user?.enterprise;
    const explicit = featureMapHas(enterprise?.features, "warehouse");
    if (explicit !== null) return explicit;
    return enterpriseHas3pl(enterprise, ctx?.user?.enterpriseId);
  },

  // INV.1 / INV.2 — hold other people's stock. Requires warehouse on.
  "inventory.3pl": (ctx) => {
    if (isDevWarehouseFresh()) {
      const s = getDevWarehouseState();
      return !!s.warehouseEnabled && !!s.threePlEnabled;
    }
    const enterprise = ctx?.user?.enterprise;
    if (!enterpriseHasWarehouse(enterprise, ctx?.user?.enterpriseId)) {
      return false;
    }
    const features = enterprise?.features;
    if (Array.isArray(features)) return features.includes("3pl");
    if (features && typeof features === "object" && "3pl" in features) {
      return !!features["3pl"];
    }
    if (isDevDomain || isBespokeThreadsDomain) return true;
    return enterpriseHas3pl(enterprise, ctx?.user?.enterpriseId);
  },

  // ASST.3 — native Ask panel (`POST /assist`). GA. Flip
  // assist.manowarRag to force the hosted iframe if Ask breaks.
  "assist.enabled": true,

  // Force the hosted Manowar iframe even if assist.enabled is on.
  // Emergency rollback only. When this is on, the Ask panel is hidden.
  "assist.manowarRag": false,

  // Supplier-sourced inventory sync. Available to every account; nothing
  // syncs until the account turns it on in Settings, Inventory sync.
  "inventory.supplierSync": (ctx) =>
    Boolean(ctx?.user?.enterpriseId || ctx?.user?.enterprise?.id),

  // Stock types §2 — house finished goods follow the warehouse program.
  // 3PL on implies FG on. Leftover finished_goods is grandfathered.
  "inventory.finished_goods": (ctx) => {
    const features = ctx?.user?.enterprise?.features;
    const has = (key) => {
      if (Array.isArray(features)) return features.includes(key);
      if (features && typeof features === "object") return !!features[key];
      return false;
    };
    return has("3pl") || has("finished_goods");
  },
};

const resolveFlag = (raw, ctx) => {
  if (typeof raw === "boolean") return raw;
  if (typeof raw === "function") return !!raw(ctx);
  if (raw === "staging") return isStagingTenant();
  // Unknown / typo'd values default off — fail closed. If a flag is
  // missing from the registry, the consumer gets `false`; nothing
  // dangerous renders by accident.
  return false;
};

/**
 * Resolve every registered flag for the current user. Memoised on
 * `user` so the resolver function reference is stable across renders
 * when the user object itself hasn't changed (Redux gives us referential
 * equality on identical user state by default).
 */
export const useFeatureFlags = () => {
  const user = useSelector((s) => s?.currentUser?.user) || {};
  const enterprise = useSelector((s) => s?.currentUser?.enterprise) || {};
  const [freshTick, setFreshTick] = useState(0);
  useEffect(() => {
    const onChange = () => setFreshTick((n) => n + 1);
    window.addEventListener(DEV_WAREHOUSE_FRESH_EVENT, onChange);
    return () => window.removeEventListener(DEV_WAREHOUSE_FRESH_EVENT, onChange);
  }, []);
  return useMemo(() => {
    const ctx = {
      user: {
        ...user,
        enterpriseId: user?.enterpriseId || enterprise?.id || null,
        enterprise,
      },
    };
    const resolved = {};
    for (const [name, raw] of Object.entries(FLAG_REGISTRY)) {
      resolved[name] = resolveFlag(raw, ctx);
    }
    return resolved;
  }, [user, enterprise, freshTick]);
};

/**
 * Resolve a single flag. Sugar over `useFeatureFlags()[name]` for the
 * common single-flag consumer.
 */
export const useFeatureFlag = (name) => useFeatureFlags()[name];
